import type { ChauffeurPage } from "../slug-chauffeur";

import heroImage from "@/assets/images/chauffeur/services/chauffeur-services.png";
import { cityPriceFaq } from "../slugs/cityPricing";

const venceEn: ChauffeurPage = {
  slug: "vence",
  title: "VTC & private chauffeur in Vence",
  subtitle:
    "Private chauffeur in Vence: the Matisse Chapel, the historic old town, Place du Peyra, the gateway to the backcountry and a ~35 min transfer to Nice airport.",
  keywords: [
    "vtc vence",
    "private chauffeur vence",
    "matisse chapel chauffeur",
    "vence nice airport transfer",
  ],
  hero: {
    title: "VTC & private chauffeur in Vence",
    subtitle:
      "The historic town and its Matisse Chapel, at the doorway to the Nice backcountry: a chauffeur for the city and the hill roads.",
    keywords: [
      "VTC Vence",
      "Matisse Chapel chauffeur",
      "Nice airport transfer",
      "backcountry chauffeur",
    ],
    imageSrc: heroImage.src,
    imageAlt: "VTC & private chauffeur in Vence",
    badges: ["Matisse Chapel", "Historic old town", "~35 min from the airport"],
  },
  benefits: {
    heading: "Why a chauffeur in Vence",
    items: [
      {
        title: "Matisse’s Chapel of the Rosary",
        description:
          "Vence’s jewel only opens on set time slots: the chauffeur plans the visit around its hours and drops you at the door, with no time wasted.",
      },
      {
        title: "Pedestrian old town",
        description:
          "Place du Peyra and its fountain, ancient gates and lanes closed to cars: drop-off as close as possible to the gates, pick-up as you leave the old town.",
      },
      {
        title: "Gateway to the backcountry",
        description:
          "Vence opens onto Tourrettes-sur-Loup, the Gorges du Loup and Coursegoules: a chauffeur who knows the mountain roads and their hairpins.",
      },
      {
        title: "~35 minutes from the airport",
        description:
          "A direct transfer to and from Nice Côte d’Azur airport, with flight tracking and a welcome on arrival.",
      },
    ],
  },
  services: {
    heading: "Chauffeur services in Vence",
    services: [
      {
        title: "Historic old-town visits",
        text: "Drop-off at the old-town gates, waiting while you wander around Place du Peyra and the cathedral, pick-up on request.",
      },
      {
        title: "Matisse Chapel",
        text: "A run planned around the Chapel of the Rosary’s opening hours, with drop-off at the entrance and waiting time included.",
      },
      {
        title: "Nice airport transfers",
        text: "Vence ⇄ Nice airport in about 35 minutes, via La Gaude and the A8, with flight tracking.",
      },
      {
        title: "A day in the backcountry",
        text: "Hourly hire to string together Tourrettes-sur-Loup, the Gorges du Loup and the perched villages, at your own pace.",
      },
      {
        title: "Evenings & Vence dining",
        text: "Old-town restaurants and nearby tables: an evening there and back with no driving of your own.",
      },
    ],
  },
  locations: {
    heading: "Places & pick-up points",
    items: [
      {
        name: "Place du Peyra",
        description:
          "The heart of the historic town, with its fountain and market.",
      },
      {
        name: "Porte du Peyra",
        description:
          "Entrance to the old town, as close as possible to the pedestrian lanes.",
      },
      {
        name: "Chapel of the Rosary",
        description:
          "The chapel decorated by Matisse, on the Saint-Jeannet road.",
      },
      {
        name: "Cathedral of the Nativity",
        description:
          "A Romanesque church in the centre, with its mosaics and altarpiece.",
      },
      {
        name: "Château de Villeneuve",
        description: "The Émile Hugues Foundation, an art-exhibition venue.",
      },
      {
        name: "Gorges du Loup road",
        description:
          "Towards Tourrettes-sur-Loup and the high country, heading for the Loup.",
      },
    ],
  },
  generic: {
    heading: "A chauffeur for Vence and the high country",
    subheading:
      "A living town at the foot of the baous, between the Matisse Chapel and the backcountry roads.",
    paragraphs: [
      "Vence is more than a sightseeing stop: it is a lived-in town, ringed by ramparts, where cars stop at the gates. The chauffeur leaves you at the threshold of the old town for a stroll around Place du Peyra or a visit to the cathedral, then picks you up with parking never entering the equation.",
      "KS Rent Services offers fixed fares set in advance — including Vence ⇄ Nice airport — and hourly or daily hire, with an immediate quote. Our bilingual French/English chauffeurs, based on the French Riviera, hold a rating of 5.0/5 from 38 Google reviews.",
      "Vence also serves as a springboard into the backcountry: Tourrettes-sur-Loup, the Gorges du Loup, Coursegoules and the passes. On those winding roads a dedicated chauffeur drives while you take in the scenery, there and back.",
    ],
    bullets: [
      "Drop-off at the town gates, pick-up on request",
      "Fixed fares & hourly hire, immediate quote",
      "Bilingual FR/EN chauffeurs, 5.0/5 from 38 Google reviews",
    ],
  },
  process: {
    heading: "Book a chauffeur in Vence",
    steps: [
      "Share your programme: Matisse Chapel, old town, backcountry or airport transfer.",
      "We confirm the vehicle and the fixed fare or hourly hire, chapel hours included.",
      "The chauffeur drops you at the town gates or at the address you want.",
      "Visit at your own pace, pick-up on request for the rest of the programme.",
    ],
  },
  faq: [
    cityPriceFaq("en", "Vence"),
    {
      q: "How long does it take to reach Nice airport from Vence?",
      a: "About 35 minutes via La Gaude and the A8. The chauffeur tracks your flight and adjusts the departure time for traffic on the run down to the coast.",
    },
    {
      q: "Can you arrange a visit to the Matisse Chapel?",
      a: "Yes. The Chapel of the Rosary only opens on set time slots; we plan the run around its hours, with drop-off at the entrance and waiting time included.",
    },
    {
      q: "Do you serve the backcountry and the Gorges du Loup?",
      a: "Yes, on daily hire to link Tourrettes-sur-Loup, the Gorges du Loup and Coursegoules, with a chauffeur used to the mountain roads.",
    },
    {
      q: "How far in advance should I book?",
      a: "Book early in spring and summer, Vence’s busy seasons; last-minute requests are handled subject to availability.",
    },
    {
      q: "Do your chauffeurs speak English?",
      a: "Yes, they are bilingual French/English and comfortable with the international visitors passing through Vence.",
    },
    {
      q: "Which vehicles do you offer for Vence?",
      a: "A Mercedes sedan, a van or an SUV depending on the group, with vehicles at ease both in town and on the backcountry roads.",
    },
  ],
  relatedLinks: [
    { label: "Nice Airport rates and routes", href: "/en/chauffeur/airport" },
    { label: "Nice chauffeur rates", href: "/en/chauffeur/nice" },
    {
      label: "Chauffeur hire on the Riviera",
      href: "/en/chauffeur/french-riviera",
    },
    {
      label: "Car rental with delivery in Vence",
      href: "/en/location/car-rental-vence",
    },
  ],
  cta: {
    title: "Your private chauffeur in Vence",
    text: "Matisse Chapel, historic town or a run into the backcountry: book your chauffeur.",
    primaryHref: "/en/reservation",
    primaryLabel: "Book a chauffeur",
  },
  seo: {
    title: "Private chauffeur in Vence 24/7 — car service & transfers",
    description:
      "Private car service in Vence: Matisse Chapel, historic old town, backcountry and a ~35 min transfer to Nice airport. 5.0/5 from 38 reviews.",
    canonical: "/en/chauffeur/vence",
  },
};

export default venceEn;
